import { z } from 'zod';
import type { DriverManifest } from '@casa/driver-sdk';
import { DRIVER_CONFIG_SCHEMAS, DRIVER_SOURCE_SCHEMAS, type BuiltInDriverName } from './config.ts';

// The manifests of the built-in drivers: the same format and the same gate as installed drivers
// (proposal §6.3, "no privileged second path"), without the consent step, since they ship with the
// app. Each reaches only the device or server its configuration names. A separate entry point
// (`@casa/drivers/manifests`), for the page: it carries zod, which the driver Worker does not need.

// The service types are those the systems announced on the owner's LAN (seen from D1, 2026-10-16);
// the AirGradient announced none. iOS browses only types listed in its Info.plist (apps/ios/project.yml).
const DETAILS: Readonly<Record<BuiltInDriverName, { name: string; description: string; configHosts: string[]; hosts?: string[]; ws?: boolean; tcp?: boolean; mdns?: string }>> = {
    homewizardp1: { name: 'HomeWizard P1 meter', description: 'Power, energy and water from the local API (v1).', configHosts: ['host'], mdns: '_hwenergy._tcp' },
    anna: { name: 'Plugwise Anna', description: 'Thermostat and boiler readings from the Smile gateway.', configHosts: ['host'], mdns: '_plugwise._tcp' },
    airgradient: { name: 'AirGradient', description: 'Air quality from the sensor\'s local API.', configHosts: ['host'] },
    hubitat: { name: 'Hubitat Elevation', description: 'Devices through the Maker API, with live events from the hub.', configHosts: ['host'], ws: true, mdns: '_hubitat._tcp' },
    influx: { name: 'InfluxDB', description: 'InfluxQL queries over the v1 query API.', configHosts: ['url'] },
    evcc: { name: 'EVCC', description: 'Chargers, vehicles, solar, grid and dynamic prices from evcc, with its charge modes.', configHosts: ['url'] },
    openweathermap: { name: 'OpenWeatherMap', description: 'Current weather, forecasts by the hour, day and night, and week, and warnings (One Call API 4.0).', configHosts: [], hosts: ['api.openweathermap.org'] },
    // Its realtime socket: socket.io over a WebSocket on the same host (homey-driver HO-5).
    homey: { name: 'Homey', description: 'Devices of a Homey Pro through its local API, with live updates.', configHosts: ['host'], ws: true, mdns: '_homey._tcp' },
    // Its WebSocket API on the same host and port (home-assistant-driver HA-5).
    homeassistant: { name: 'Home Assistant', description: 'Entities of a Home Assistant, with live updates, and its services as commands.', configHosts: ['host'], ws: true, mdns: '_home-assistant._tcp' },
    hue: { name: 'Philips Hue', description: 'Lights, rooms, zones, scenes and sensors of a Hue Bridge, with live updates.', configHosts: ['host'], mdns: '_hue._tcp' },
    // Only the Tilez Hub its connection names (public-services.md SV-20).
    'casa-weather': { name: 'Tilez weather', description: 'Current weather, forecasts and warnings from Tilez Cloud, without a key of your own. Weather: OpenWeatherMap.', configHosts: ['server'] },
    'casa-prices': { name: 'Tilez energy prices', description: 'Day-ahead electricity prices for a zone from Tilez Cloud, as your contract prices them. Prices: Energy-Charts (Fraunhofer ISE), CC BY 4.0.', configHosts: ['server'] },
    // The brands' common login site and API (myvaillant-driver spec MV-3).
    myvaillant: {
        name: 'Vaillant, Bulex, … heating (myVAILLANT cloud)',
        description: 'Zones as thermostats, hot water and the system of a Vaillant group heating controller (Bulex MiPro Sense, Vaillant sensoCOMFORT, …) through its internet gateway and the brand’s cloud.',
        configHosts: [],
        hosts: ['identity.vaillant-group.com', 'api.vaillant-group.com'],
    },
    // ebusd's TCP port on the host its connection names (ebusd-driver spec EB-2). It announces nothing.
    ebusd: {
        name: 'eBUS heating (ebusd)',
        description: 'Zones as thermostats, hot water and the system of an eBUS heating controller (Bulex MiPro Sense, Vaillant sensoCOMFORT, …) through ebusd on the LAN, and any other eBUS value.',
        configHosts: ['host'],
        tcp: true,
    },
    // Only the Healthbox its connection names (renson-driver spec RE-2).
    renson: { name: 'Renson Healthbox', description: 'Air flow, temperature, humidity, CO₂ and VOC per room of a Renson Healthbox 3.0 ventilation unit, from its local API, with a room’s boost.', configHosts: ['host'] },
    // No network at all (demo-driver spec DM-2).
    demo: { name: 'Demo home', description: 'A simulated home: lights, climate, solar and a battery, meters, a car charger, the weather and prices, without any device.', configHosts: [] },
    // The brands' login sites (`UCONNECT_BRANDS`), their API gateways, and the AWS identity pools.
    uconnect: {
        name: 'Fiat, Jeep, Alfa Romeo, … (Uconnect)',
        description: 'Battery or fuel, range, charging and climate of Stellantis cars through the Uconnect cloud, with start charging and climate on and off.',
        configHosts: [],
        hosts: [
            'loginmyuconnect.fiat.com', 'login-us.fiat.com', 'login-iap.fiat.com', 'login-stage-us.fiat.com',
            'login.alfaromeo.com', 'login-us.alfaromeo.com', 'login-iap.alfaromeo.com',
            'login-us.chrysler.com', 'login-stage-us.chrysler.com',
            'login.maserati.com', 'login-us.maserati.com', 'accounts.au1.gigya.com',
            'login.jeep.com', 'login-us.jeep.com', 'login-iap.jeep.com',
            'login-us.dodge.com', 'login-us.ramtrucks.com',
            '*.fcagcv.com', 'cognito-identity.eu-west-1.amazonaws.com', 'cognito-identity.us-east-1.amazonaws.com',
        ],
    },
};

function manifestOf(id: BuiltInDriverName) : DriverManifest {
    const details = DETAILS[id];

    return {
        id,
        name: details.name,
        version: '1.0.0',
        description: details.description,
        entry: 'driver.js',
        permissions: { hosts: details.hosts ?? [], configHosts: details.configHosts, ws: details.ws ?? false, tcp: details.tcp ?? false },
        configSchema: z.toJSONSchema(DRIVER_CONFIG_SCHEMAS[id]) as Record<string, unknown>,
        sourceSchema: z.toJSONSchema(DRIVER_SOURCE_SCHEMAS[id]) as Record<string, unknown>,
        ...(details.mdns ? { discovery: { mdns: details.mdns, configField: 'host' } } : {}),
    };
}

export const BUILT_IN_MANIFESTS: Readonly<Record<BuiltInDriverName, DriverManifest>> = {
    homewizardp1: manifestOf('homewizardp1'),
    anna: manifestOf('anna'),
    airgradient: manifestOf('airgradient'),
    hubitat: manifestOf('hubitat'),
    influx: manifestOf('influx'),
    evcc: manifestOf('evcc'),
    homey: manifestOf('homey'),
    homeassistant: manifestOf('homeassistant'),
    hue: manifestOf('hue'),
    openweathermap: manifestOf('openweathermap'),
    uconnect: manifestOf('uconnect'),
    myvaillant: manifestOf('myvaillant'),
    ebusd: manifestOf('ebusd'),
    renson: manifestOf('renson'),
    demo: manifestOf('demo'),
    'casa-weather': manifestOf('casa-weather'),
    'casa-prices': manifestOf('casa-prices'),
};
